import { createRouter, createWebHashHistory } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';

const routes = [
  { path: '/',         name: 'home',    component: () => import('@/views/HomeView.vue') },
  { path: '/setup',    name: 'setup',   component: () => import('@/views/SetupView.vue') },
  { path: '/order',    name: 'order',   component: () => import('@/views/OrderRevealView.vue') },
  { path: '/game',     name: 'game',    component: () => import('@/views/GameView.vue') },
  { path: '/summary',  name: 'summary', component: () => import('@/views/SummaryView.vue') },
  { path: '/admin',         name: 'admin',     component: () => import('@/views/AdminView.vue') },
  { path: '/admin/new',     name: 'admin-new', component: () => import('@/views/AdminEditorView.vue') },
  { path: '/admin/edit/:id',name: 'admin-edit',component: () => import('@/views/AdminEditorView.vue') },
  { path: '/admin/grid',    name: 'admin-grid',component: () => import('@/views/AdminGridView.vue') },
  { path: '/admin/users',   name: 'admin-users',component: () => import('@/views/AdminUsersView.vue') },
  { path: '/admin/stats',   name: 'admin-stats',component: () => import('@/views/AdminStatsView.vue') },
  { path: '/login',         name: 'login',     component: () => import('@/views/LoginView.vue') },
  { path: '/change-password',name: 'change-pw',component: () => import('@/views/ChangePasswordView.vue') },
  { path: '/profile',       name: 'profile',   component: () => import('@/views/ProfileView.vue') },
  { path: '/leaderboard',   name: 'leaderboard',component: () => import('@/views/LeaderboardView.vue') },
  { path: '/rules',         name: 'rules',     component: () => import('@/views/RulesView.vue') },
  { path: '/lobby',         name: 'lobby',     component: () => import('@/views/LobbyView.vue') },
  { path: '/friends',       name: 'friends-menu', component: () => import('@/views/FriendsMenuView.vue') },
  { path: '/matchmaking',   name: 'matchmaking-menu', component: () => import('@/views/MatchmakingMenuView.vue') },
  { path: '/matchmaking/rooms', name: 'matchmaking-rooms', component: () => import('@/views/PublicRoomsView.vue') },
  { path: '/solo',             name: 'solo-menu',       component: () => import('@/views/SoloMenuView.vue') },
  { path: '/solo/daily',       name: 'solo-daily',      component: () => import('@/views/SoloDailyView.vue') },
  { path: '/solo/daily/review',name: 'solo-daily-review',component: () => import('@/views/SoloDailyReviewView.vue') },
  { path: '/solo/leaderboard', name: 'solo-leaderboard',component: () => import('@/views/SoloLeaderboardView.vue') },
  { path: '/solo/rules',       name: 'solo-rules',      component: () => import('@/views/SoloRulesView.vue') },
  { path: '/solo/badges',      name: 'solo-badges',     component: () => import('@/views/SoloBadgesView.vue') },
  { path: '/duels',            name: 'duel-menu',       component: () => import('@/views/DuelMenuView.vue') },
  { path: '/duels/search',     name: 'duel-search',     component: () => import('@/views/DuelSearchView.vue') },
  { path: '/duels/inbox',      name: 'duel-inbox',      component: () => import('@/views/DuelInboxView.vue') },
  { path: '/duels/stats',      name: 'duel-stats',      component: () => import('@/views/DuelStatsView.vue') },
  { path: '/duels/vs/:opponentId', name: 'duel-head-to-head', component: () => import('@/views/DuelHeadToHeadView.vue') },
  { path: '/duels/:id',        name: 'duel-detail',     component: () => import('@/views/DuelResultView.vue') },
  { path: '/duels/:id/play',   name: 'duel-play',       component: () => import('@/views/DuelRoundPlayView.vue') },
  { path: '/contacts',         name: 'contacts',        component: () => import('@/views/ContactsView.vue') },
];

export const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// Routes accessibles sans authentification.
const PUBLIC_ROUTES = new Set(['login']);
// Routes réservées aux managers/admins (banque de questions).
const MANAGER_ROUTES = new Set(['admin', 'admin-new', 'admin-edit']);
// Routes réservées aux admins/super_admins (gestion users + stats).
const ADMIN_ROUTES = new Set(['admin-grid', 'admin-users', 'admin-stats']);

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  // Au premier appel, on tente une init (lit le cookie httpOnly via /api/auth/me).
  if (!auth.initialized) {
    await auth.init();
  }

  const isPublic = to.name && PUBLIC_ROUTES.has(String(to.name));

  // Pas connecté + route protégée → login
  if (!auth.user && !isPublic) {
    return { name: 'login', query: { next: to.fullPath } };
  }

  // Déjà connecté et veut aller sur /login → renvoie vers la home (ou redirect)
  if (auth.user && to.name === 'login') {
    const next = (to.query.next as string) || '/';
    return next;
  }

  // Doit changer son mot de passe → force la route
  if (auth.user?.must_change_password && to.name !== 'change-pw') {
    return { name: 'change-pw' };
  }

  // Routes manager/admin : seuls les rôles habilités y accèdent
  if (to.name && MANAGER_ROUTES.has(String(to.name)) && !auth.isManager) {
    return { name: 'home' };
  }
  // Routes admin (gestion users) : admin/super_admin uniquement
  if (to.name && ADMIN_ROUTES.has(String(to.name)) && !auth.isAdmin) {
    return { name: 'home' };
  }
});
